import { PropsWithChildren, ReactElement, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Language } from "shared/model/languages";

import { LoadingScreen } from "./loading-screen";
import {
  CreateUserRequest,
  RegisterUserRequest,
  UpdateUserAttributesDto,
} from "../api/generated/backend";
import { toTenantIdHeader, useApiMutation, useApiQuery } from "../api/use-api";
import { queryClient } from "../query-client";
import { ME_KEY, USER_EXISTS_KEY } from "../types/query-keys";
import {
  userHasConfirmedAccount,
  userHasFinishedSignup,
} from "../util/data-layer-actions";
import { useTenantId } from "../util/use-active-tenant-id";
import { useUser } from "../util/use-user";

export const CreateUserGuard: React.FC<PropsWithChildren> = ({ children }) => {
  const tenantId = useTenantId();
  const user = useUser();

  const {
    i18n: { language: selectedLanguage },
  } = useTranslation();

  const { data: userExistsResponse } = useApiQuery(
    "backend",
    (client) => client.userExists(),
    USER_EXISTS_KEY,
  );

  const { mutate: updateUserAttributes } = useApiMutation(
    "backend",
    (api) => (updateUserAttributesDto: UpdateUserAttributesDto) =>
      api.updateUserAttributes({
        updateUserAttributesDto,
        ...toTenantIdHeader(tenantId),
      }),
    undefined,
    undefined,
    {
      onSuccess() {
        queryClient.invalidateQueries(ME_KEY(tenantId));
      },
    },
  );

  const { mutate: createUser } = useApiMutation(
    "backend",
    (client) => (params: CreateUserRequest) => client.createUser(params),
    undefined,
    undefined,
    {
      onSuccess: () => {
        userHasConfirmedAccount();
        queryClient.invalidateQueries(USER_EXISTS_KEY);
        updateUserAttributes({
          language: (selectedLanguage ??
            localStorage.getItem("language") ??
            undefined) as Language | undefined,
        });
      },
    },
  );

  const { mutate: unlinkTemporaryToken } = useApiMutation(
    "backend",
    (client) => () => client.unlinkTemporaryToken(),
    undefined,
    undefined,
    {
      onSuccess() {
        queryClient.invalidateQueries(ME_KEY(tenantId));
      },
    },
  );

  const { mutate: registerUser } = useApiMutation(
    "backend",
    (client) => (params: RegisterUserRequest) => client.registerUser(params),
    undefined,
    undefined,
    {},
  );

  const { mutate: markSignUpAsDone } = useApiMutation(
    "backend",
    (api) => () => api.markSignUpAsDone(toTenantIdHeader(tenantId)),
    undefined,
    undefined,
    {
      onSuccess: userHasFinishedSignup,
    },
  );

  useEffect(() => {
    if (user && !user?.userAttributes?.finishedSignUp) {
      unlinkTemporaryToken();
      markSignUpAsDone();
    }
  }, [markSignUpAsDone, unlinkTemporaryToken, user]);

  useEffect(() => {
    if (userExistsResponse && !userExistsResponse.userExists) {
      createUser(toTenantIdHeader(tenantId));
    }
  }, [createUser, registerUser, tenantId, userExistsResponse]);

  return userExistsResponse?.userExists ? (
    (children as ReactElement)
  ) : (
    <LoadingScreen />
  );
};
